interface SecureCookieOptions {
  expires?: number // Days
  path?: string
  domain?: string
  sameSite?: 'strict' | 'lax' | 'none'
  encrypt?: boolean
  encryptionSecret?: string
}

export const useCookiee = {
  set<T = any>(
    name: string,
    value: T,
    options?: SecureCookieOptions,
  ): void {
    const encrypt = options?.encrypt ?? false
    const secret = options?.encryptionSecret ?? 'secret_rintisan_no_one_know'

    // Convert value to string
    const stringValue = typeof value === 'string' ? value : JSON.stringify(value)

    // Encrypt if enabled
    const finalValue = encrypt ? this._encode(stringValue, secret) : stringValue
    const config = useRuntimeConfig()

    const isProduction = config.public.envSite === 'production'

    // Cookie options for Nuxt useCookie (convert days to seconds)
    const cookie = useCookie(name, {
      maxAge: (options?.expires ?? 7) * 86400, // Convert days to seconds
      path: options?.path ?? '/',
      domain: options?.domain,
      secure: isProduction,
      sameSite: options?.sameSite ?? 'strict',
    })

    cookie.value = finalValue
  },

  get<T = any>(name: string, options?: SecureCookieOptions): T | null {
    const cookie = useCookie<string | null>(name, {
      default: () => null,
      watch: false,
    })

    const rawValue = cookie.value

    if (!rawValue)
      return null

    const encrypt = options?.encrypt ?? false
    const secret = options?.encryptionSecret ?? 'secret_rintisan_no_one_know'

    try {
      // Decrypt if enabled
      const decodedValue = encrypt ? this._decode(rawValue, secret) : rawValue

      // Try to parse as JSON
      return JSON.parse(decodedValue) as T
    }
    catch {
      // Return as string if parsing fails
      return rawValue as T
    }
  },

  remove(name: string, options?: Pick<SecureCookieOptions, 'path' | 'domain'>): void {
    const cookie = useCookie<string | null>(name, {
      maxAge: -1, // Expire immediately
      path: options?.path ?? '/',
      domain: options?.domain,
    })

    cookie.value = null
  },

  getAll(): Record<string, string> {
    if (import.meta.client) {
      const cookies: Record<string, string> = {}
      document.cookie.split(';').forEach((cookie) => {
        const [name, value] = cookie.split('=').map(c => c.trim())
        if (name && value) {
          cookies[name] = decodeURIComponent(value)
        }
      })
      return cookies
    }
    return {}
  },
  _encode(value: string, secret: string): string {
    // On server, just return the value unencrypted to avoid Buffer issues
    // Cookie encryption is not critical for functionality
    if (import.meta.server) {
      return value
    }

    try {
      // Client-side encryption only
      const encoder = new TextEncoder()
      const data = encoder.encode(value)
      const keyData = Array.from(encoder.encode(secret))

      if (keyData.length === 0)
        return value

      // XOR encryption
      const encrypted = Array.from(data).map((byte, i) =>
        byte ^ keyData[i % keyData.length]!,
      )

      // Convert to base64
      return btoa(String.fromCharCode(...encrypted))
    }
    catch {
      return value // Fallback to unencrypted
    }
  },

  _decode(value: string, secret: string): string {
    // On server, just return the value as-is
    if (import.meta.server) {
      return value
    }

    try {
      // Client-side decryption only
      const encrypted = atob(value)
      const encryptedBytes = Array.from(encrypted).map(c => c.charCodeAt(0))

      // XOR decryption
      const encoder = new TextEncoder()
      const keyData = Array.from(encoder.encode(secret))

      if (keyData.length === 0)
        return value

      const decrypted = encryptedBytes.map((byte, i) =>
        byte ^ keyData[i % keyData.length]!,
      )

      // Use TextDecoder for proper UTF-8 decoding
      const decoder = new TextDecoder()
      return decoder.decode(new Uint8Array(decrypted))
    }
    catch {
      return value // Return as-is if decryption fails
    }
  },

}
